import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';
import { TYPE_LABEL, formatDate, formatViews } from '@/lib/content';

interface ArticleCardProps {
  article: Article;
  variant?: 'vertical' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'vertical' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row"
      >
        <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-32 sm:w-48">
          <img
            src={article.cover}
            alt={article.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
              {TYPE_LABEL[article.type]}
            </span>
            <span>{article.subcategory}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{formatDate(article.date)}</span>
          </div>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line text-sm"></i>
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-sm"></i>
              {formatViews(article.views)} 阅读
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-100">
        <img
          src={article.cover}
          alt={article.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded bg-accent-500 px-2 py-1 text-xs font-semibold text-background-50">
          {TYPE_LABEL[article.type]}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-time-line text-sm"></i>
            {formatDate(article.date)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line text-sm"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}